/**
 * Rebuy Child Products Handler
 * Handles grouping and display of child products (back engraving) with main products
 */

class RebuyChildProductsHandler {
  constructor() {
    this.ensureGlobalHelpers();
  }

  ensureGlobalHelpers() {
    if (!window.rebuyCartHelpers) {
      window.rebuyCartHelpers = {
        // main product is the one that has `_Pendant Back Side` (per new requirement)
        isMainProduct: this.isMainProduct.bind(this),
        hasChildProducts: this.hasChildProducts.bind(this),
        getChildProducts: this.getChildProducts.bind(this),
        groupedItems: this.groupedItems.bind(this),
        getAllCartItems: this.getAllCartItems.bind(this),
        decreaseItemEvent: this.decreaseItemEvent?.bind(this),
        increaseItemEvent: this.increaseItemEvent?.bind(this),
      };
    }
  }

  /**
   * In the new behavior the "main" product is the one that has the
   * `_Pendant Back Side` property. Provide an explicit helper for that.
   */
  isMainProduct(item) {
    if (!item || !item.properties) return false;
    return item.properties.hasOwnProperty('_Pendant Back Side');
  }

  /**
   * Common logic for handling quantity changes on main products and their children
   */
  handleQuantityChange(item, actionFunction, items) {
    if (!item || !actionFunction) return;
    
    if (this.isMainProduct(item) && item.properties?._Timestamp) {
      const childTimestamp = item.properties._Timestamp;
      const childItem = items.find(i =>
        !this.isMainProduct(i) &&
        i.properties?._Timestamp === childTimestamp
      );
      if (childItem) {
        actionFunction(childItem);
      }
    }
    
    actionFunction(item);
  }


  decreaseItemEvent({item, decreaseFunction, items}){
    this.handleQuantityChange(item, decreaseFunction, items);
  }

  increaseItemEvent({item, increaseFunction, items}){
    this.handleQuantityChange(item, increaseFunction, items);
  }

  /**
   * Check if main product has child products with same timestamp
   */
  hasChildProducts(item, allItems = null) {
    // Only main products can have children
    if (!this.isMainProduct(item)) return false;
    if (!item.properties || !item.properties._Timestamp) return false;

    const timestamp = item.properties._Timestamp;
    const items = allItems || this.getAllCartItems();

    return items.some(cartItem =>
      cartItem.key !== item.key &&
      !this.isMainProduct(cartItem) &&
      cartItem.properties?._Timestamp === timestamp,
    );
  }

  /**
   * Get child products for a main product
   */
  getChildProducts(item, allItems = null) {
    if (!this.isMainProduct(item)) return [];
    if (!item.properties || !item.properties._Timestamp) return [];

    const timestamp = item.properties._Timestamp;
    const items = allItems || this.getAllCartItems();

    return items.filter(cartItem =>
      cartItem.key !== item.key &&
      !this.isMainProduct(cartItem) &&
      cartItem.properties?._Timestamp === timestamp,
    );
  }

 

  /**
   * Group items - returns only main products (child products are handled separately)
   * @param {Array} items - Array of cart items to filter
   */
  groupedItems(items = null) {
    const allItems = items || this.getAllCartItems();
    // Build a set of timestamps that belong to main products
    const mainTimestamps = new Set(
      allItems
        .filter(i => this.isMainProduct(i) && this.getItemTimestamp(i))
        .map(i => this.getItemTimestamp(i))
    );

    return allItems.filter(item => {
      if (this.isMainProduct(item)) return true;
      const ts = this.getItemTimestamp(item);
      if (ts && mainTimestamps.has(ts)) return false;
      return true;
    });
  }

  /**
   * Get all cart items from Rebuy
   */
  getAllCartItems() {
    if (window.rebuy && window.rebuy.cart && window.rebuy.cart.data) {
      return window.rebuy.cart.data.items || [];
    }
    return [];
  }

  /**
   * Get timestamp from item properties (internal helper)
   */
  getItemTimestamp(item) {
    return item.properties?._Timestamp || null;
  }
}

// Initialize immediately
window.rebuyChildProductsHandler = new RebuyChildProductsHandler();

// Also initialize when DOM is ready (fallback)
document.addEventListener('DOMContentLoaded', () => {
  if (!window.rebuyChildProductsHandler) {
    window.rebuyChildProductsHandler = new RebuyChildProductsHandler();
  }
});